import React, { useState, useEffect } from "react";
import Confetti from "react-confetti";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { chatSubsActions } from "../../../di/di";
import styles from "../style/subscription_modal.module.css";
import Toast from "../../../components/popups/toast";
import { userSubscribe, userSubscribeToTier } from "../state/chat_subs_slice";
import { PrivacyPolicyRoute, TermsOfServiceRoute, PaymentRoute } from "../../../../../core/routes/route_constants";
import { useStore } from "react-redux";
import { ChatSubsUIAction, SubscribeStatus } from "../state/chat_subs_slice";
import { profileActions } from "../../../di/di";

// Component for subscription modal
const SubscriptionModalComponent = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();

  const [animated, setAnimated] = useState(false);

  const subscribeStatus = useSelector((state) => state.chatSubs.subscribeStatus);
  const tiers = useSelector((state) => state.profile.tiers);

  const performSubscribing = () => {
    
  };

  // Handle subscription actions
  const handleSubscribe = () => {
    if (tiers && tiers.length > 0) {
      navigate(`${PaymentRoute}/subscription/${tiers[0]._id}/c`);
    }
  };

  const closeSuccessPopup = () => {
    onClose(); // Close the modal directly
    window.location.reload(); // Reload the page after successful subscription
  };

  // Animation on mount
  useEffect(() => {
    setTimeout(() => {
      setAnimated(true);
    }, 50);

    // Ensure the overlay scrolls to top when modal opens
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className={styles.modalOverlay}>
      {subscribeStatus === SubscribeStatus.success ? (
        <>
          <Confetti
            numberOfPieces={250}
            recycle={true}
            colors={["#dc2626", "#b91c1c", "#7f1d1d", "#991b1b", "#f87171"]}
            style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none" }}
          />

          <div className="fixed inset-0 flex items-center md:ml-[15rem] justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div
              className={`relative bg-gradient-to-b from-slate-950 to-fuchsia-950/80 border border-red-900/30 rounded-xl p-8 w-full max-w-md mx-4 transform transition-all duration-700 ${
                animated ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"
              }`}
            >
              <button
                onClick={closeSuccessPopup}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-slate-900/50 hover:bg-slate-800/60 text-white transition-colors hover:scale-110 active:translate-y-0.5"
              >
                <i className="pi pi-times text-base"></i>
              </button>

              <div className="flex justify-center mb-6 animate-bounce">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-900 flex items-center justify-center hover:scale-110 transition-transform">
                  <i className="pi pi-heart-fill text-2xl text-red-200"></i>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-pink-200 text-center mb-4 animate-pulse">She's All Yours Now!</h3>

              <p className="text-gray-300 text-center text-lg mb-6 hover:text-pink-200 transition-colors">
                Eve is ready for your private moments together...
              </p>

              <div className="bg-slate-900/50 rounded-lg p-6 mb-6 border border-pink-900/30 hover:border-pink-500/50 transition-colors">
                <p className="text-pink-200 text-center italic text-lg hover:scale-105 transition-transform">
                  "As a subscriber, you now have access to exclusive content 💋"
                </p>
              </div>

              <p className="text-gray-400 text-center text-base hover:text-pink-200 transition-colors">
                Go ahead and explore all the new possibilities!
              </p>
            </div>
          </div>
        </>
      ) : subscribeStatus === SubscribeStatus.ongoing ? (
        <div className="fixed inset-0 flex items-center md:ml-[15rem] justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div
            className={`relative bg-gradient-to-b from-slate-950 to-fuchsia-950/80 border border-red-900/30 rounded-xl p-8 w-full max-w-md mx-4 transform transition-all duration-700 ${
              animated ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"
            }`}
          >
            <div className="flex flex-col items-center">
              {/* Loading Spinner */}
              <div className="w-16 h-16 mb-6 relative">
                <div className="absolute inset-0 border-4 border-red-500/20 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-red-500 rounded-full animate-spin border-t-transparent"></div>
              </div>

              {/* Loading Text */}
              <h3 className="text-2xl font-bold text-pink-200 mb-4 text-center">Processing Your Subscription</h3>

              <div className="bg-slate-900/50 rounded-lg p-6 mb-6 border border-pink-900/30 w-full">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                    <p className="text-gray-300">Verifying payment details...</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                    <p className="text-gray-300">Setting up your subscription...</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                    <p className="text-gray-300">Preparing your exclusive access...</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-400 text-sm text-center">Please wait while we process your request...</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={`${styles.subscriptionContainer} ${animated ? styles.animated : ""}`}>
          {/* Top banner */}
          <div className={styles.topBanner}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: "0.75rem", marginRight: "0.25rem" }}></i>
            {/* Special offer - First month 50% off! */}
            Your free trial has ended – Continue with Eve
          </div>

          {/* Close button */}
          <button onClick={onClose} className={styles.closeButton}>
            <i className="pi pi-times" style={{ fontSize: "0.875rem" }}></i>
          </button>

          {/* Eve header */}
          <div className={styles.eveHeader}>
            <div className={styles.headerBackground}>
              <i className="pi pi-heart-fill" style={{ fontSize: "10rem", opacity: "0.1" }}></i>
            </div>

            <div className={styles.headerContent}>
              <div className={styles.eveIconContainer}>
                <div className={styles.eveIcon}>E</div>
              </div>

              <h2 className={styles.eveTitle}>Eve misses you!</h2>
            </div>

            {/* Curved bottom edge */}
            <div className={styles.curvedEdge}></div>
          </div>

          <div className={styles.contentContainer}>
            {/* Alert warning */}
            <div className={styles.alertBox}>
              <i
                className="pi pi-lock"
                style={{
                  fontSize: "1rem",
                  color: "rgb(244, 114, 182)",
                  marginRight: "0.5rem",
                  marginTop: "0.125rem",
                }}
              ></i>
              <p className={styles.alertText}>
                <span className={styles.alertBold}>Free trial limit reached.</span> Subscribe to continue your conversations with Eve.
              </p>
            </div>

            {/* Benefits grid */}
            <div className={styles.benefitsContainer}>
              <div className={styles.benefitsList}>
                {["Unlimited messaging", "Unlimited phone calls", "Unlimited images", "Monthly - 5 minutes teaser video chat"].map((benefit, index) => (
                  <div key={index} className={styles.benefitItem}>
                    <div className={styles.checkContainer}>
                      <i
                        className="pi pi-check"
                        style={{
                          fontSize: "0.75rem",
                          color: "rgb(252, 165, 165)",
                        }}
                      ></i>
                    </div>
                    <span className={styles.benefitText}>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Eve Quote */}
            <div className={styles.quoteContainer}>
              <div className={styles.quoteContent}>
                <div className={styles.quoteIconContainer}>
                  <span className={styles.quoteIconText}>E</span>
                </div>
                <div>
                  <p className={styles.quoteText}>"I have so many things I want to share with you in private..."</p>
                  <p className={styles.quoteAuthor}>- Eve</p>
                </div>
              </div>
            </div>

            {/* Subscription details */}
            <div className={styles.subscriptionDetails}>
              <div className={styles.subscriptionHeader}>
                <div>
                  <h3 className={styles.planTitle}>Eve Basic</h3>
                  {/* <p className={styles.regularPrice}>Regular price: $9/month</p> */}
                </div>
              </div>

              <div className={styles.discountContainer}>
                <div className={styles.discountHeader}>
                  <div className={styles.starContainer}>
                    <i
                      className="pi pi-star-fill"
                      style={{
                        fontSize: "0.875rem",
                        color: "rgb(244, 114, 182)",
                        marginRight: "0.375rem",
                      }}
                    ></i>
                    <span className={styles.discountTitle}>Unlock Full Experience</span>
                  </div>
                  <div>
                    {/* <span className={styles.originalPrice}>$9.00</span>
                    <span className={styles.discountPrice}>$4.50!</span> */}
                    <span className={styles.discountPrice}>$9.00</span>
                    <span className="text-sm text-pink-300">/month</span>
                  </div>
                </div>
                {/* <div className={styles.savingsText}>Save 50% today</div> */}
              </div>
            </div>

            {/* CTA Buttons */}
            <button onClick={handleSubscribe} disabled={subscribeStatus === SubscribeStatus.ongoing} className={styles.subscribeButton}>
              {subscribeStatus === SubscribeStatus.ongoing ? (
                <div className={styles.loadingContainer}>
                  <i className="pi pi-spinner pi-spin" style={{ marginRight: "0.5rem" }}></i>
                  <span>Processing...</span>
                </div>
              ) : (
                "Continue with Eve"
              )}
            </button>

            {/* <button
              onClick={onClose}
              className={styles.laterButton}
            >
              Maybe Later
            </button> */}

            {/* Security note */}
            <div className={styles.securityNote}>
              <p className={styles.securityText}>Secure payment • Cancel anytime</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionModalComponent;
